import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';

/**
 * Handles room-invite and OAuth deep links (RONDA_ONLINE.md sección 14 /
 * Fase 9c): `rondaybaciga://join-room?code=XXXX`,
 * `https://rondaybaciga.on.shiper.app/join/XXXX` (Android App Links / iOS
 * Universal Links — see app-links.routes.ts and the AndroidManifest/
 * entitlements intent-filters), and
 * `rondaybaciga://oauth2/callback?access_token=...&refresh_token=...`
 * (the latter built server-side by auth.controller.ts's redirectWithTokens
 * — see env.oauthRedirectNative). Capacitor's `appUrlOpen` fires for both
 * the custom scheme AND verified https App Links, so both are matched here.
 * Mounted once, always — not tied to any particular route — same
 * `App.addListener` + cleanup pattern already used by
 * VersionCheckManager.jsx for `appStateChange`.
 *
 * The custom-scheme regex deliberately avoids `new URL(...).host`/
 * `.pathname`: WHATWG URL parsing of non-http(s) custom schemes is not
 * consistent across platforms/engines. The https case is a normal URL, so
 * it's safe to parse with `new URL(...)`.
 */
function extractJoinCode(url) {
    const customSchemeMatch = /^rondaybaciga:\/\/join-room\?(.+)$/i.exec(url);
    if (customSchemeMatch) {
        return new URLSearchParams(customSchemeMatch[1]).get('code');
    }
    if (/^https:\/\//i.test(url)) {
        const match = /^\/join\/([^/?#]+)/i.exec(new URL(url).pathname);
        if (match) return decodeURIComponent(match[1]);
    }
    return null;
}

function extractOAuthTokens(url) {
    const match = /^rondaybaciga:\/\/oauth2\/callback\?(.+)$/i.exec(url);
    if (!match) return null;
    const params = new URLSearchParams(match[1]);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (!accessToken || !refreshToken) return null;
    return { accessToken, refreshToken };
}

export default function DeepLinkListener() {
    const navigate = useNavigate();
    const { loginWithTokens } = useAuth();

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return undefined;

        const listener = App.addListener('appUrlOpen', ({ url }) => {
            const joinCode = extractJoinCode(url);
            if (joinCode) {
                navigate(`/online/join/${encodeURIComponent(joinCode)}`);
                return;
            }

            const oauthTokens = extractOAuthTokens(url);
            if (oauthTokens) {
                loginWithTokens(oauthTokens.accessToken, oauthTokens.refreshToken)
                    .then(() => navigate('/online', { replace: true }))
                    .catch(() => navigate('/online/login', { replace: true }));
            }
        });

        return () => {
            listener.then((handle) => handle.remove());
        };
    }, [navigate, loginWithTokens]);

    return null;
}
